import React from 'react';
import { defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { AppConstants, DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import useListModal from '@hooks/useListModal';
import useTranslate from '@hooks/useTranslate';
import { BaseTable } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import restaurantSelectors from '@selectors/restaurant';
import { formatMoney } from '@utils';
import { Modal } from 'antd';

import IngredientsForm from './IngredientsForm';

import styles from './ingredients.module.scss';
const message = defineMessages({
    objectName: 'Ingredients',
});
const IngredientsListPage = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const currentRestaurant = useSelector(restaurantSelectors.selectDetailRestaurant);
    const restaurantDetail = currentRestaurant || {};
    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
        detail,
        titleModal,
        executeSave,
        loadingSave,
        form,
        openModal,
        onShowModal,
        onCancelModal,
        setIsChangedFormValues,
        onSave,
        isEditing,
    } = useListModal({
        apiConfig: apiConfig.ingredients,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
        },
    });

    const columns = [
        {
            title: translate.formatMessage(commonMessage.plu),
            dataIndex: 'plu',
            width: 100,
        },
        {
            title: translate.formatMessage(commonMessage.name),
            dataIndex: 'foodConditionName',
        },
        {
            title: translate.formatMessage(commonMessage.price),
            dataIndex: 'foodConditionPrice',
            width: 150,
            render: (value) => {
                const settingsStr = restaurantDetail?.settings;
                const settings = settingsStr && settingsStr !== "{}" ? JSON.parse(settingsStr) : {};
                return formatMoney(value, settings);
            },
        },
        mixinFuncs.renderStatusColumn({ width: '120px' }),
        mixinFuncs.renderActionColumn(
            {
                edit: true,
                delete: true,
            },
            { width: '120px' },
        ),
    ];

    const searchFields = [
        {
            key: 'name',
            placeholder: translate.formatMessage(commonMessage.name),
        },
        {
            key: 'plu',
            placeholder: translate.formatMessage(commonMessage.plu),
            type: FieldTypes.STRING, 
        },
    ];

    return (
        <PageWrapper routes={[ { breadcrumbName: translate.formatMessage(message.objectName) } ]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                actionBar={mixinFuncs.renderActionBar({
                    objectName: translate.formatMessage(message.objectName),
                })}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        style={{ cursor: 'pointer' }}
                        loading={loading || loadingSave}
                    />
                }
            />
            {openModal && (
                <BaseModal
                    title={
                        detail
                            ? translate.formatMessage(commonMessage.updateIngredients)
                            : translate.formatMessage(commonMessage.createIngredients)
                    }
                    width={1000}
                    isModalVisible={openModal}
                    handleCancel={onCancelModal}
                    isEditing={!!detail}
                    form={form}
                    loading={loadingSave}
                    customStyle={{ height: '750px' }}
                >
                    <IngredientsForm
                        detail={detail}
                        form={form}
                        isEditing={isEditing}
                        onSubmit={onSave}
                        actions={mixinFuncs.renderActions()}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default IngredientsListPage;
